import type { CheckoutInterval, CheckoutPlan } from '@kimuno/api-sdk/lib/types/requests/Billing';
import { useEffect, useRef, useState } from 'react';

import service from '../../../../services/billing.service';
import { mapErrorToKind, resolveCheckoutAttribution } from '../checkout.helpers';
import type { CheckoutErrorKind } from '../checkout.types';

interface UseCheckoutSessionsParams {
	plan: CheckoutPlan | null;
	// SM-375: seeds the single session's initial interval. NOT a reactive
	// dependency — an interval switch swaps the live session's line items in place
	// (see <CheckoutPaymentElement>), so it must not recreate the session (which
	// would remount <PaymentElement> and wipe entered card details). Captured via
	// a ref, so it only ever seeds the mount-time interval.
	interval: CheckoutInterval | null;
	trial: boolean;
	// SM-273: NOT a reactive dependency — a toggle must not recreate the session
	// (that remounts <PaymentElement>, wiping entered card details). Captured via
	// a ref, so it only ever seeds the metadata with the mount-time state.
	// SM-284: the page pushes the authoritative final state via
	// `updateCheckoutSession` right before confirm.
	newsletterConsent: boolean;
	/** False while the checkout link is invalid (no plan/interval) — skips creation. */
	enabled: boolean;
	/**
	 * SM-345 — the email the sessions should be bound to. `null` = the common,
	 * up-front (unbound) create: Stripe auto-creates the customer. A non-null
	 * value is a churned (`reusable-customer`) email: both interval sessions are
	 * recreated with `email` so the backend binds them to the existing Stripe
	 * customer (and suppresses the trial). Changing this recreates the pair;
	 * leaving it `null` (the default for every new/unknown email) never does, so
	 * the common path never remounts or flickers.
	 */
	boundEmail: string | null;
}

interface UseCheckoutSessionsResult {
	/** Client secret of the single live checkout session, or null until it lands. */
	secret: string | null;
	/**
	 * ISO 3166-1 alpha-2 country the legacy API resolved from the client IP (the
	 * same value used for tax-rate matching), used to seed the Stripe
	 * billing-address country default so the first invoice's country matches the
	 * applied tax rate (SM-252). Defaults to `'DE'` until the session lands and
	 * whenever the response carries no country.
	 */
	country: string;
	errorCode: CheckoutErrorKind | null;
	setErrorCode: (kind: CheckoutErrorKind | null) => void;
	/**
	 * SM-345 — the `boundEmail` the currently-live session was created with
	 * (`null` = unbound). The page compares it against the desired `boundEmail`
	 * to Pay-guard: completing is only safe once the live session reflects the
	 * intended binding.
	 */
	boundTo: string | null;
	/** SM-345 — true while a (re)creation is in flight (Pay-guard). */
	pending: boolean;
}

const DEFAULT_COUNTRY = 'DE';

/**
 * Owns the single Stripe checkout session (SM-375). We create ONE session for
 * the mount-time interval and keep a single <PaymentElement> mounted, so the
 * user can switch interval or apply/remove a promo without reloading Stripe or
 * losing card input — both are applied in place on the live session (interval:
 * `runServerUpdate` line-item swap; promo: `applyPromotionCode`; see
 * <CheckoutPaymentElement>). The session is only recreated when plan or trial
 * changes; interval and newsletter changes never recreate it.
 *
 * The session is deliberately created WITHOUT a promo code: Stripe treats
 * `discounts` (a baked-in code) and `allow_promotion_codes` (needed for
 * `applyPromotionCode`) as mutually exclusive (kimuno stripe adapter). Baking a
 * link-borne promo in here would speed first paint but permanently disable
 * in-place apply/remove for that session — so a URL promo is applied in place on
 * mount instead, costing one extra round-trip but keeping the code editable.
 */
export function useCheckoutSessions({
	plan,
	interval,
	trial,
	newsletterConsent,
	enabled,
	boundEmail,
}: UseCheckoutSessionsParams): UseCheckoutSessionsResult {
	const [secret, setSecret] = useState<string | null>(null);
	const [country, setCountry] = useState<string>(DEFAULT_COUNTRY);
	const [errorCode, setErrorCode] = useState<CheckoutErrorKind | null>(null);
	const [boundTo, setBoundTo] = useState<string | null>(null);
	const [pending, setPending] = useState(false);

	// Latest interval + consent, read (not depended on) when the session is
	// created — see the param docs above (SM-375 / SM-273).
	const intervalRef = useRef(interval);
	intervalRef.current = interval;
	const newsletterConsentRef = useRef(newsletterConsent);
	newsletterConsentRef.current = newsletterConsent;

	useEffect(() => {
		const seedInterval = intervalRef.current;
		if (!plan || !enabled || !seedInterval) return;
		let cancelled = false;
		setErrorCode(null);
		setPending(true);

		(async () => {
			try {
				// SM-309: resolved per session-creation (not a hook param) so a
				// plan/trial recreation picks up fresh last-touch values. Omitted
				// entirely when nothing resolves (no cookie / declined consent / clean
				// URL).
				const attribution = resolveCheckoutAttribution();
				// SM-345: a churned (`reusable-customer`) email is passed so the backend
				// binds the session to the existing Stripe customer (and suppresses the
				// trial); `null` is the common up-front create (Stripe auto-creates).
				const session = await service.createCheckoutSession({
					plan,
					interval: seedInterval,
					trial,
					newsletterConsent: newsletterConsentRef.current,
					...(attribution ? { attribution } : {}),
					...(boundEmail ? { email: boundEmail } : {}),
				});
				if (cancelled) return;
				setSecret(session.clientSecret);
				setCountry(session.country ?? DEFAULT_COUNTRY);
				setBoundTo(boundEmail);
				setErrorCode(null);
			} catch (err) {
				if (cancelled) return;
				setSecret(null);
				setErrorCode(mapErrorToKind(err));
			} finally {
				if (!cancelled) setPending(false);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [plan, trial, enabled, boundEmail]);

	return { secret, country, errorCode, setErrorCode, boundTo, pending };
}
